import { HTTPError, KyInstance, Options } from 'ky';

/**
 * Type guard to check if an object has a specific property
 */
const hasProperty = <T extends string>(obj: unknown, prop: T): obj is Record<T, unknown> => {
  return typeof obj === 'object' && obj !== null && prop in obj;
};

/**
 * Safely obtains the value of a property from an object
 */
const getProperty = (obj: unknown, key: string): unknown => {
  if (typeof obj === 'object' && obj !== null && key in obj) {
    return (obj as Record<string, unknown>)[key];
  }
  return undefined;
};

/**
 * Safely extracts a string from an unknown object
 */
const extractString = (obj: unknown, defaultValue: string | null = ''): string | null => {
  return typeof obj === 'string' ? obj : defaultValue;
};

/**
 * Interface for the error extraction result
 */
export interface ExtractedErrorInfo {
  message: string;
  requestId: string | null;
  statusCode: number | null;
}

/**
 * Safely extracts error information from different types of errors
 * Supports: HTTPError (ky), Axios errors, custom API errors, and generic errors
 */
export const extractErrorInfo = (
  error: unknown,
  defaultMessage = 'Ha ocurrido un error',
): ExtractedErrorInfo => {
  const result: ExtractedErrorInfo = {
    message: defaultMessage,
    requestId: null,
    statusCode: null,
  };

  if (error == null) {
    return result;
  }

  if (error instanceof HTTPError) {
    result.statusCode = error.response.status;
    return result;
  }

  if (error instanceof Error) {
    result.message = error.message || defaultMessage;
  }

  if (typeof error === 'object') {
    const err = error as Record<string, unknown>;

    if (hasProperty(err, 'request_id')) {
      result.requestId = extractString(err.request_id, null);
    } else {
      const response = getProperty(err, 'response');
      const responseData = getProperty(response, 'data');
      const data = getProperty(err, 'data');

      if (responseData) {
        result.requestId = extractString(getProperty(responseData, 'request_id'), null);
      } else if (data) {
        result.requestId = extractString(getProperty(data, 'request_id'), null);
      }
    }

    if (hasProperty(err, 'message') && typeof err.message === 'string') {
      result.message = err.message;
    } else {
      const response = getProperty(err, 'response');
      const responseData = getProperty(response, 'data');
      const data = getProperty(err, 'data');

      if (responseData) {
        const msg = extractString(getProperty(responseData, 'message'), null);
        if (msg) result.message = msg;
      } else if (data) {
        const msg = extractString(getProperty(data, 'message'), null);
        if (msg) result.message = msg;
      }
    }

    const status = getProperty(err, 'status');
    const statusCode = getProperty(err, 'statusCode');
    const response = getProperty(err, 'response');
    const responseStatus = getProperty(response, 'status');

    if (typeof status === 'number') {
      result.statusCode = status;
    } else if (typeof responseStatus === 'number') {
      result.statusCode = responseStatus;
    } else if (typeof statusCode === 'number') {
      result.statusCode = statusCode;
    }
  }

  return result;
};

/**
 * Extracts a user-facing error message from an API response object,
 * prioritizing validation details in data array over generic message.
 */
export const extractApiErrorMessage = (
  response: unknown,
  fallbackMessage = 'Ha ocurrido un error',
): string => {
  if (response && typeof response === 'object') {
    const res = response as Record<string, unknown>;
    if (Array.isArray(res.data) && res.data.length > 0 && typeof res.data[0] === 'string') {
      return res.data[0];
    }
    if (typeof res.message === 'string' && res.message) {
      return res.message;
    }
  }
  return fallbackMessage;
};

export const buildSearchParams = (params: object) => {
  const searchParams: Record<string, string | number | boolean> = {};
  for (const key in params) {
    const value = (params as Record<string, unknown>)[key];
    if (value !== undefined) {
      searchParams[key] = value as string | number | boolean;
    }
  }
  return searchParams;
};

/**
 * Extended error class that carries the structured errors array from the API response body
 */
export class ApiImportError extends Error {
  readonly errors: Array<{ field?: string; message?: string; sku?: string; error?: string }>;
  readonly requestId: string | null;

  constructor(
    message: string,
    errors: Array<{ field?: string; message?: string; sku?: string; error?: string }>,
    requestId?: string | null,
  ) {
    super(message);
    this.name = 'ApiImportError';
    this.errors = errors;
    this.requestId = requestId ?? null;
  }
}

export const createUserFriendlyError = async (
  error: unknown,
  customMessage?: string,
): Promise<Error> => {
  if (error instanceof HTTPError) {
    try {
      const responseBody = (
        error.data !== undefined ? error.data : await error.response.json().catch(() => null)
      ) as Record<string, unknown> | null;

      if (Array.isArray(responseBody?.errors) && responseBody.errors.length > 0) {
        const message =
          typeof responseBody.message === 'string'
            ? responseBody.message
            : (customMessage ?? 'Error al procesar la solicitud.');
        return new ApiImportError(
          message,
          responseBody.errors,
          typeof responseBody.request_id === 'string' ? responseBody.request_id : null,
        );
      }

      if (
        Array.isArray(responseBody?.data) &&
        responseBody.data.length > 0 &&
        typeof responseBody.data[0] === 'string'
      ) {
        return new Error(responseBody.data[0]);
      }

      if (responseBody?.message && typeof responseBody.message === 'string') {
        return new Error(responseBody.message);
      }
    } catch {
      // Response body could not be parsed as JSON, fall through to status-based messages
    }

    const statusMessages: Record<number, string> = {
      400: customMessage || 'Los datos enviados no son válidos. Por favor, revisa la información.',
      401: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
      403: 'No tienes permisos para realizar esta acción.',
      404: 'El recurso solicitado no fue encontrado.',
      409: 'Ya existe un registro con esta información.',
      422: customMessage || 'Los datos no son válidos. Por favor, revisa los campos.',
      429: 'Demasiadas peticiones. Por favor, espera un momento e intenta de nuevo.',
      500: 'Error interno del servidor. Por favor, intenta más tarde.',
      502: 'Servicio no disponible temporalmente.',
      503: 'El servicio está en mantenimiento. Intenta más tarde.',
    };

    const userMessage =
      statusMessages[error.response.status] ||
      customMessage ||
      'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.';
    return new Error(userMessage);
  }

  if (error instanceof Error) {
    if (error.message.includes('Request failed') || error.message.includes('http')) {
      return new Error(customMessage || 'Ha ocurrido un error. Por favor, intenta de nuevo.');
    }
    return error;
  }

  return new Error(customMessage || 'Ha ocurrido un error inesperado.');
};

export const handleRequest = async <T>(
  http: KyInstance,
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  url: string,
  options: Options = {},
): Promise<T> => {
  try {
    const response = await http[method](url, options);
    return response.json();
  } catch (error) {
    throw await createUserFriendlyError(error);
  }
};

/**
 * Result of a binary (blob) download request
 */
export interface BlobResponseResult {
  blob: Blob;
  filename: string | null;
}

/**
 * Extracts the filename from a `Content-Disposition` header value
 * e.g. `attachment; filename="inventory-report.xlsx"` -> `inventory-report.xlsx`
 */
export const extractFilenameFromContentDisposition = (
  contentDisposition: string | null,
): string | null => {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Performs a request expecting a binary (blob) response, such as file downloads.
 */
export const handleBlobRequest = async (
  http: KyInstance,
  method: 'get' | 'post' | 'put' | 'delete' | 'patch',
  url: string,
  options: Options = {},
): Promise<BlobResponseResult> => {
  try {
    const response = await http[method](url, options);
    const blob = await response.blob();
    const filename = extractFilenameFromContentDisposition(
      response.headers.get('content-disposition'),
    );
    return { blob, filename };
  } catch (error) {
    throw await createUserFriendlyError(error);
  }
};
